/**
 * Nucery Bundle Builder — "Compose your edit"
 * Client-side edit cart: pick variants from collection-fed cards, build an
 * "edit" with live totals / tier rewards / perks / animations, then commit the
 * whole edit to the real Shopify cart via /cart/add.js.
 *
 * Tier discount %s are visual estimates only — real pricing is enforced by
 * Shopify Automatic Discounts the merchant sets up separately.
 *
 * Hooks: data-bb-* (no collision with the site cart drawer's data-nucery-*).
 */
(function () {
  'use strict';

  var root, config, catalog;
  var qvEntry = null; // product entry currently shown in the quick-view modal
  var state = {}; // variantId -> { variantId, productId, title, meta, image, price, qty }
  var products = {}; // productId -> { product, chips:[], sel:{}, variant }
  var tiers = []; // sorted ascending by req
  var prevCount = 0;
  var prevTierIdx = -1;
  var prevTotal = 0;

  function qs(sel, ctx) {
    return (ctx || root).querySelector(sel);
  }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || root).querySelectorAll(sel));
  }

  function ensureRoot() {
    root = document.getElementById('NuceryBundleBuilder');
    // Flag the page so CSS can hide overlapping third-party widgets (e.g. the
    // Dondy WhatsApp chat bubble) only where the bundle builder is present.
    if (root) document.documentElement.classList.add('bb-has-builder');
    return root;
  }

  function readJSON(sel) {
    var el = root && root.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ---------- money ---------- */
  function stripHtml(html) {
    var d = document.createElement('div');
    d.innerHTML = String(html == null ? '' : html);
    return (d.textContent || d.innerText || '').trim();
  }
  // Active display currency + base→active rate from Shopify's storefront
  // multi-currency. Liquid bakes prices in the SHOP base currency; the storefront
  // converts to the customer's chosen currency client-side via Shopify.currency.
  // Our prices aren't .money spans the theme converter would touch, so we convert
  // here the same way the theme does.
  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency || config.shopCurrency || 'USD';
  }
  function activeRate() {
    var r = (window.Shopify && window.Shopify.currency) ? parseFloat(window.Shopify.currency.rate) : NaN;
    return (isFinite(r) && r > 0) ? r : 1;
  }
  function formatMoneyRaw(cents) {
    cents = Number(cents) || 0;
    var shopCur = (config.shopCurrency || config.currency || 'USD').toString();
    var active = String(activeCurrency());
    var fmt = config.moneyFormat;
    var shopifyOk = window.Shopify && typeof Shopify.formatMoney === 'function' && fmt;
    // Viewing the base currency → use the theme's money format as-is.
    if (active.toUpperCase() === shopCur.toUpperCase()) {
      if (shopifyOk) return Shopify.formatMoney(cents, fmt);
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + active;
      }
    }
    // Different display currency → convert base→active by the storefront rate.
    var converted = cents * activeRate();
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(converted / 100);
    } catch (e) {
      return (converted / 100).toFixed(2) + ' ' + active;
    }
  }
  function money(cents) {
    return stripHtml(formatMoneyRaw(cents));
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function str(key) {
    return (config.strings && config.strings[key] != null) ? config.strings[key] : '';
  }

  /* ---------- option swatch colours ---------- */
  var SWATCH_COLORS = {
    platinum: 'linear-gradient(135deg,#e9e9ec,#c9c9cf)',
    silver: 'linear-gradient(135deg,#e9e9ec,#c9c9cf)',
    'white gold': 'linear-gradient(135deg,#efeff2,#d6d6db)',
    gold: 'linear-gradient(135deg,#e7c987,#c69a4c)',
    'yellow gold': 'linear-gradient(135deg,#e7c987,#c69a4c)',
    '18k gold': 'linear-gradient(135deg,#e7c987,#c69a4c)',
    rose: 'linear-gradient(135deg,#e9c4b4,#cf9d8a)',
    'rose gold': 'linear-gradient(135deg,#e9c4b4,#cf9d8a)',
    bronze: 'linear-gradient(135deg,#c89b6a,#9c7240)',
    copper: 'linear-gradient(135deg,#d99b76,#b06a45)',
    champagne: 'linear-gradient(135deg,#f0e2c4,#d9c79c)',
    black: '#1d1d1d',
    white: '#fff',
    ivory: '#f4ecdd',
    cream: '#f4ecdd',
    pearl: 'radial-gradient(circle at 35% 30%,#fff,#e6e2da)',
    blue: '#2f5fbf',
    'navy blue': '#22335c',
    navy: '#22335c',
    'sky blue': '#7db4e6',
    teal: '#1f8a8a',
    green: '#3f8f5b',
    'emerald green': '#2f7a52',
    emerald: '#2f7a52',
    olive: '#7a7a3f',
    sage: '#a7b59a',
    red: '#b53245',
    burgundy: '#6b2233',
    wine: '#6b2233',
    pink: '#e6a0b5',
    purple: '#7a4f9c',
    grey: '#9a9a9a',
    gray: '#9a9a9a',
    brown: '#7a5230',
    beige: '#e3d6bf'
  };
  function colorFor(value) {
    return SWATCH_COLORS[String(value || '').trim().toLowerCase()] || null;
  }

  /* ---------- catalog → products map ---------- */
  function buildProducts() {
    products = {};
    var list = (catalog && catalog.products) || [];
    list.forEach(function (p) {
      var entry = products[p.id];
      if (!entry) {
        entry = products[p.id] = { product: p, chips: [], sel: {}, variant: null };
        // default selection = first available variant (else first)
        var def = null;
        for (var i = 0; i < p.variants.length; i++) {
          if (p.variants[i].available) { def = p.variants[i]; break; }
        }
        if (!def) def = p.variants[0];
        if (def) {
          (def.options || []).forEach(function (v, idx) { entry.sel[idx] = v; });
          entry.variant = def;
        }
      }
      if (entry.chips.indexOf(p.chip) === -1) entry.chips.push(p.chip);
    });
  }

  function optionValues(product, pos) {
    // distinct values at option index `pos`, in first-seen order
    var seen = {}, out = [];
    product.variants.forEach(function (v) {
      var val = (v.options || [])[pos];
      if (val != null && !seen[val]) { seen[val] = 1; out.push(val); }
    });
    return out;
  }

  function valueAvailable(product, pos, val) {
    return product.variants.some(function (v) {
      return (v.options || [])[pos] === val && v.available;
    });
  }

  function resolveVariant(entry) {
    var p = entry.product;
    var match = p.variants.filter(function (v) {
      return (v.options || []).every(function (val, idx) {
        return entry.sel[idx] == null || entry.sel[idx] === val;
      });
    });
    // prefer an available match
    var avail = match.filter(function (v) { return v.available; });
    entry.variant = avail[0] || match[0] || p.variants[0] || null;
    return entry.variant;
  }

  function variantMeta(product, variant) {
    if (!variant) return '';
    if (variant.title && variant.title !== 'Default Title') return variant.title;
    return product.type || '';
  }

  // Hide junk meta values: blank, "Default Title", or numeric placeholder types
  // (e.g. "0", "7") that some products carry from imports.
  function cleanMeta(s) {
    s = String(s == null ? '' : s).trim();
    if (!s || /^\d+$/.test(s) || s.toLowerCase() === 'default title') return '';
    return s;
  }

  /* ---------- card rendering ---------- */
  function cardHTML(entry) {
    var p = entry.product;
    var v = entry.variant;
    // Image slider: use all product images (fallback to the single featured image).
    var imgs = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
    var img = '';
    if (imgs.length > 1) {
      var slides = imgs.map(function (src) {
        return '<img src="' + escapeAttr(src) + '" loading="lazy" alt="' + escapeAttr(p.title) + '">';
      }).join('');
      var dots = imgs.map(function (_s, di) {
        return '<span class="bb__thumb-dot' + (di === 0 ? ' on' : '') + '"></span>';
      }).join('');
      img = '<div class="bb__thumb-track" data-bb-track>' + slides + '</div>' +
            '<div class="bb__thumb-dots" data-bb-dots>' + dots + '</div>';
    } else if (imgs.length === 1) {
      img = '<img src="' + escapeAttr(imgs[0]) + '" width="300" height="300" loading="lazy" alt="' + escapeAttr(p.title) + '">';
    }
    var opts = '';
    var hasVisibleOpts = false;
    (p.options || []).forEach(function (o, i) {
      var vals = optionValues(p, i);
      if (vals.length <= 1) return; // hide single-value options (e.g. "Title")
      hasVisibleOpts = true;
      var sw = vals.map(function (val) {
        var sel = entry.sel[i] === val ? ' sel' : '';
        var avail = valueAvailable(p, i, val);
        var col = colorFor(val);
        var cls = 'bb__swatch' + (col ? ' has-dot' : '') + sel + (avail ? '' : ' soldout');
        var dot = col ? '<span class="bb__dot" style="background:' + col + '"></span>' : '';
        return '<button type="button" class="' + cls + '" data-bb-swatch="' + i + '" data-bb-value="' + escapeAttr(val) + '"' +
          ' title="' + escapeAttr(val) + '"' + (avail ? '' : ' aria-disabled="true"') + '>' + dot + escapeHtml(val) + '</button>';
      }).join('');
      opts += '<div class="bb__opt"><div class="bb__opt-name">' + escapeHtml(o.name) + '</div><div class="bb__swatches">' + sw + '</div></div>';
    });
    // When option swatches are shown, the variant title just duplicates them —
    // fall back to the product type (or nothing) so we don't print e.g. "White"
    // right above the COLOR: White / Brown swatches.
    var meta = cleanMeta(hasVisibleOpts ? (p.type || '') : variantMeta(p, v));
    return (
      '<div class="bb__badge">' + escapeHtml(str('inEdit')) + '</div>' +
      '<div class="bb__thumb' + (img ? '' : ' is-empty') + '" data-bb-thumb>' + img +
        '<button type="button" class="bb__qv-btn" data-bb-quick title="' + escapeAttr(str('quickView') || 'Quick view') + '" aria-label="' + escapeAttr(str('quickView') || 'Quick view') + '">' +
          '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z"/><circle cx="12" cy="12" r="3"/></svg>' +
          '<span class="bb__qv-btn-t">' + escapeHtml(str('quickView') || 'Quick view') + '</span>' +
        '</button>' +
      '</div>' +
      '<div class="bb__card-body">' +
      '<h3>' + escapeHtml(p.title) + '</h3>' +
      (meta ? '<div class="meta">' + escapeHtml(meta) + '</div>' : '') +
      opts +
      '<div class="bb__priceRow" data-bb-price></div>' +
      '<button type="button" class="bb__add" data-bb-add>' + escapeHtml(str('addToEdit')) + '</button>' +
      '</div>'
    );
  }

  function renderCardPrice(card, entry) {
    var v = entry.variant;
    var el = qs('[data-bb-price]', card);
    if (!el) return;
    if (config.bundleHidePrice) { el.innerHTML = ''; return; }
    if (!v) { el.innerHTML = ''; return; }
    var html = '<span class="bb__price">' + escapeHtml(money(v.price));
    if (v.compare && v.compare > v.price) html += ' <s>' + escapeHtml(money(v.compare)) + '</s>';
    html += '</span>';
    el.innerHTML = html;
  }

  function refreshCardState(card, entry) {
    var v = entry.variant;
    // swatch selection + availability
    qsa('[data-bb-swatch]', card).forEach(function (sw) {
      var i = parseInt(sw.getAttribute('data-bb-swatch'), 10);
      var val = sw.getAttribute('data-bb-value');
      sw.classList.toggle('sel', entry.sel[i] === val);
    });
    renderCardPrice(card, entry);
    var add = qs('[data-bb-add]', card);
    var line = v ? state[v.id] : null;
    var unavailable = !v || !v.available;
    // Is THIS product in the edit (by product id, so changing a swatch after
    // adding doesn't "unlock" the card)?
    var prodInEdit = Object.keys(state).some(function (vid) {
      return state[vid].productId === entry.product.id && state[vid].qty > 0;
    });
    if (add) {
      var atMax = totals().count >= maxTotal();
      if (unavailable) {
        add.disabled = true;
        add.classList.remove('in', 'remove');
        add.textContent = str('soldOut');
      } else if (atMax) {
        // ALL tiers unlocked (top tier reached):
        //  - product already in the edit → "Remove" (free a slot to swap)
        //  - product not in the edit → blocked ("Bundle full")
        if (prodInEdit) {
          add.classList.add('in', 'remove');
          add.disabled = false;
          add.textContent = str('removeFromEdit') || 'Remove';
        } else {
          add.classList.remove('in', 'remove');
          add.disabled = true;
          add.textContent = str('bundleFull') || str('addToEdit');
        }
      } else {
        // Below max → always an ADD action (you can add the same product again).
        add.classList.remove('remove');
        add.disabled = false;
        if (prodInEdit) {
          add.classList.add('in');
          var lq = line ? line.qty : 0;
          add.textContent = str('inEdit') + ' · ' + lq + ' · ' + str('addAnother');
        } else {
          add.classList.remove('in');
          add.textContent = str('addToEdit');
        }
      }
    }
    card.classList.toggle('in', prodInEdit);
  }

  function buildGrid() {
    var grid = qs('[data-bb-grid]');
    if (!grid) return;
    grid.innerHTML = '';
    Object.keys(products).forEach(function (pid, idx) {
      var entry = products[pid];
      var card = document.createElement('article');
      card.className = 'bb__card';
      card.setAttribute('data-bb-card', pid);
      card.setAttribute('data-bb-chips', entry.chips.join(' '));
      card.innerHTML = cardHTML(entry);
      grid.appendChild(card);
      refreshCardState(card, entry);
      initCardSlider(card);
      // staggered reveal
      var delay = Math.min(idx * 45, 600);
      setTimeout(function () { card.classList.add('reveal'); }, delay);
    });
  }

  // Card image slider: a native scroll-snap strip the shopper can scroll / swipe
  // through (desktop + touch). Mouse wheel scrolls horizontally through the
  // images, and the strip can be click-dragged. Dots reflect position and are
  // clickable.
  function initCardSlider(card) {
    var track = qs('[data-bb-track]', card);
    if (!track) return;
    var slides = qsa('img', track);
    if (slides.length < 2) return;
    var dots = qsa('[data-bb-dots] span', card);
    function setDots(i) { dots.forEach(function (d, di) { d.classList.toggle('on', di === i); }); }
    function current() { return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0; }
    function goTo(i) { track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }); }

    track.addEventListener('scroll', function () { setDots(current()); }, { passive: true });
    dots.forEach(function (d, di) {
      d.addEventListener('click', function (e) { e.stopPropagation(); goTo(di); });
    });

    // optional prev/next arrows (used by the quick-view gallery)
    var prevBtn = qs('[data-bb-arrow="prev"]', card);
    var nextBtn = qs('[data-bb-arrow="next"]', card);
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); goTo(Math.max(0, current() - 1)); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); goTo(Math.min(slides.length - 1, current() + 1)); });

    track.classList.add('bb__thumb-track--drag');

    // Mouse wheel → horizontal scroll. Translate the dominant wheel delta into a
    // horizontal scroll so a normal vertical wheel pages through the images.
    track.addEventListener('wheel', function (e) {
      var delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!delta) return;
      var atStart = track.scrollLeft <= 0;
      var atEnd = track.scrollLeft >= (track.scrollWidth - track.clientWidth - 1);
      // Only hijack the wheel while there's room to scroll in that direction,
      // so the page keeps scrolling at the strip's edges.
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
      e.preventDefault();
      track.scrollLeft += delta;
    }, { passive: false });

    // Click-drag to scroll (desktop). A small movement threshold prevents a drag
    // from being read as a click on the card's swatches / add button.
    var down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      down = true; moved = false;
      startX = e.pageX; startLeft = track.scrollLeft;
      track.classList.add('is-grabbing');
    });
    document.addEventListener('mousemove', function (e) {
      if (!down) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 3) moved = true;
      track.scrollLeft = startLeft - dx;
      e.preventDefault();
    });
    document.addEventListener('mouseup', function () {
      if (!down) return;
      down = false;
      track.classList.remove('is-grabbing');
      if (moved) goTo(current()); // snap to the nearest image after a drag
    });
    // Swallow the click that follows a real drag so it doesn't hit the buttons.
    track.addEventListener('click', function (e) {
      if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; }
    }, true);
  }

  /* ---------- quick view ---------- */
  // Interactive option swatches for the quick-view modal — clicking one changes
  // the entry's selected variant (kept in sync with the matching grid card).
  function qvOptionsHTML(entry) {
    var p = entry.product;
    var out = '';
    (p.options || []).forEach(function (o, i) {
      var vals = optionValues(p, i);
      if (vals.length <= 1) return;
      var sel = entry.sel[i];
      var sw = vals.map(function (val) {
        var col = colorFor(val);
        var avail = valueAvailable(p, i, val);
        var cls = 'bb__swatch' + (col ? ' has-dot' : '') + (sel === val ? ' sel' : '') + (avail ? '' : ' soldout');
        var dot = col ? '<span class="bb__dot" style="background:' + col + '"></span>' : '';
        return '<button type="button" class="' + cls + '" data-bb-qv-swatch="' + i + '" data-bb-value="' + escapeAttr(val) + '"' +
          ' title="' + escapeAttr(val) + '"' + (avail ? '' : ' aria-disabled="true"') + '>' + dot + escapeHtml(val) + '</button>';
      }).join('');
      out += '<div class="bb__opt"><div class="bb__opt-name">' + escapeHtml(o.name) + '</div><div class="bb__swatches">' + sw + '</div></div>';
    });
    return out;
  }

  function quickViewHTML(entry) {
    var p = entry.product;
    // gallery (same scroll-snap strip as the cards, but larger)
    var imgs = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
    var gallery = '';
    if (imgs.length > 1) {
      var slides = imgs.map(function (src) {
        return '<img src="' + escapeAttr(src) + '" loading="lazy" alt="' + escapeAttr(p.title) + '">';
      }).join('');
      var dots = imgs.map(function (_s, di) {
        return '<span class="bb__thumb-dot' + (di === 0 ? ' on' : '') + '"></span>';
      }).join('');
      var arrows =
        '<button type="button" class="bb__qv-arrow bb__qv-arrow--prev" data-bb-arrow="prev" aria-label="Previous">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>' +
        '</button>' +
        '<button type="button" class="bb__qv-arrow bb__qv-arrow--next" data-bb-arrow="next" aria-label="Next">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>' +
        '</button>';
      gallery = '<div class="bb__thumb-track" data-bb-track>' + slides + '</div>' + arrows +
                '<div class="bb__thumb-dots" data-bb-dots>' + dots + '</div>';
    } else if (imgs.length === 1) {
      gallery = '<img src="' + escapeAttr(imgs[0]) + '" loading="lazy" alt="' + escapeAttr(p.title) + '">';
    }
    var eyebrow = cleanMeta(p.type || '');
    // Price is (re)filled by refreshQuickView so it tracks the selected variant.
    var priceHtml = config.bundleHidePrice ? '' : '<div class="bb__qv-price" data-bb-qv-price></div>';
    return (
      '<div class="bb__qv-gallery bb__thumb" data-bb-qv-gallery>' + gallery + '</div>' +
      '<div class="bb__qv-info">' +
        (eyebrow ? '<div class="bb__qv-eyebrow">' + escapeHtml(eyebrow) + '</div>' : '') +
        '<h3 class="bb__qv-h">' + escapeHtml(p.title) + '</h3>' +
        priceHtml +
        '<div class="bb__qv-opts">' + qvOptionsHTML(entry) + '</div>' +
        '<button type="button" class="bb__qv-add" data-bb-qv-add>' + escapeHtml(str('qvAdd') || 'Add to cart') + '</button>' +
        (p.description ? '<div class="bb__qv-desc rte">' + p.description + '</div>' : '') +
      '</div>'
    );
  }

  function openQuickView(entry) {
    var modal = qs('[data-bb-qv]');
    var body = qs('[data-bb-qv-body]', modal);
    if (!modal || !body || !entry) return;
    qvEntry = entry;
    body.innerHTML = quickViewHTML(entry);
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bb-qv-lock');
    // wire the gallery slider (it lives in the modal, not a card)
    var gal = qs('[data-bb-qv-gallery]', modal);
    if (gal) initCardSlider(modal);
    // set the initial price + add-button state for the current variant
    refreshQuickView();
  }
  function closeQuickView() {
    var modal = qs('[data-bb-qv]');
    if (!modal) return;
    qvEntry = null;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('bb-qv-lock');
  }

  // Reflect the quick-view entry's current variant into the open modal: swatch
  // selection, price, and the add-to-edit button's label/disabled state. Mirrors
  // the grid card's refreshCardState() so the two surfaces behave the same.
  function refreshQuickView() {
    var modal = qs('[data-bb-qv]');
    if (!modal || !qvEntry) return;
    var entry = qvEntry;
    var v = entry.variant;
    // swatch selection
    qsa('[data-bb-qv-swatch]', modal).forEach(function (sw) {
      var i = parseInt(sw.getAttribute('data-bb-qv-swatch'), 10);
      var val = sw.getAttribute('data-bb-value');
      sw.classList.toggle('sel', entry.sel[i] === val);
    });
    // price
    var priceEl = qs('[data-bb-qv-price]', modal);
    if (priceEl) {
      if (config.bundleHidePrice || !v) {
        priceEl.innerHTML = '';
      } else {
        var html = '<span class="bb__price">' + escapeHtml(money(v.price));
        if (v.compare && v.compare > v.price) html += ' <s>' + escapeHtml(money(v.compare)) + '</s>';
        html += '</span>';
        priceEl.innerHTML = html;
      }
    }
    // add-to-edit button
    var add = qs('[data-bb-qv-add]', modal);
    if (add) {
      var unavailable = !v || !v.available;
      var atMax = totals().count >= maxTotal();
      var prodInEdit = Object.keys(state).some(function (vid) {
        return state[vid].productId === entry.product.id && state[vid].qty > 0;
      });
      if (unavailable) {
        add.disabled = true;
        add.textContent = str('soldOut') || 'Sold out';
      } else if (atMax && !prodInEdit) {
        add.disabled = true;
        add.textContent = str('bundleFull') || str('qvAdd') || 'Add to cart';
      } else {
        add.disabled = false;
        add.textContent = str('qvAdd') || 'Add to cart';
      }
    }
  }

  // Sale countdown bars: fixed end date OR per-visitor "evergreen" timer (stored
  // in localStorage so it survives reloads and resets when it runs out).
  function initCountdowns() {
    qsa('[data-bb-countdown]').forEach(function (el) {
      if (el._bbCd) return;
      el._bbCd = true;
      var mode = el.getAttribute('data-mode') || 'evergreen';
      var expire = el.getAttribute('data-expire') || 'keep';
      var deadline = null;
      if (mode === 'fixed') {
        var raw = (el.getAttribute('data-end') || '').trim();
        var parsed = raw ? Date.parse(raw.replace(' ', 'T')) : NaN;
        if (!isNaN(parsed)) deadline = parsed;
      }
      if (!deadline) {
        var hours = Number(el.getAttribute('data-hours')) || 24;
        var key = 'bb_cd_' + (el.getAttribute('data-id') || 'x');
        var saved = 0;
        try { saved = parseInt(localStorage.getItem(key), 10) || 0; } catch (e) {}
        var nowMs = Date.now();
        if (!saved || saved <= nowMs) {
          saved = nowMs + hours * 3600000;
          try { localStorage.setItem(key, String(saved)); } catch (e2) {}
        }
        deadline = saved;
      }
      var dEl = qs('[data-bb-cd="d"]', el), hEl = qs('[data-bb-cd="h"]', el),
          mEl = qs('[data-bb-cd="m"]', el), sEl = qs('[data-bb-cd="s"]', el);
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      var timer;
      function tick() {
        var totalSec = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
        var s = totalSec % 60;
        var m = Math.floor(totalSec / 60) % 60;
        var h, d;
        if (dEl) { d = Math.floor(totalSec / 86400); h = Math.floor(totalSec / 3600) % 24; dEl.textContent = pad(d); }
        else { h = Math.floor(totalSec / 3600); }
        if (hEl) hEl.textContent = pad(h);
        if (mEl) mEl.textContent = pad(m);
        if (sEl) sEl.textContent = pad(s);
        if (totalSec <= 0) {
          clearInterval(timer);
          if (expire === 'hide') el.style.display = 'none';
        }
      }
      tick();
      timer = setInterval(tick, 1000);
    });
  }

  function filterGrid(chip) {
    qsa('[data-bb-card]').forEach(function (card) {
      var chips = (card.getAttribute('data-bb-chips') || '').split(' ');
      var show = chip === 'all' || chips.indexOf(chip) !== -1;
      card.style.display = show ? '' : 'none';
    });
  }

  /* ---------- totals / tiers ---------- */
  function lines() {
    return Object.keys(state).map(function (k) { return state[k]; }).filter(function (l) { return l.qty > 0; });
  }
  function totals() {
    var count = 0, subtotal = 0, value = 0;
    lines().forEach(function (l) {
      count += l.qty;
      subtotal += l.qty * l.price;
      // "value" prefers the compare-at price (where the original price lives on a
      // 0.00 bundle variant) so we can still show a savings figure in bundle mode.
      var unit = (l.compare && l.compare > l.price) ? l.compare : l.price;
      value += l.qty * unit;
    });
    var tier = null, tierIdx = -1;
    tiers.forEach(function (t, i) { if (count >= t.req) { tier = t; tierIdx = i; } });
    var pct = tier ? Number(tier.pct) || 0 : 0;
    var bundle = config.pricingMode === 'bundle';
    var discount, total, was;
    if (bundle) {
      was = value || subtotal;
      if (tier && tier.bundlePrice > 0) {
        total = tier.bundlePrice;             // flat tier price
        discount = Math.max(0, was - total);  // savings vs. original value
      } else {
        total = was;                          // below the first tier — no flat price yet
        discount = 0;
      }
    } else {
      discount = Math.round(subtotal * pct / 100);
      total = subtotal - discount;
      was = subtotal;
    }
    return { count: count, subtotal: subtotal, value: value, was: was, tier: tier, tierIdx: tierIdx, pct: pct, discount: discount, total: total, bundle: bundle };
  }

  // Total pieces allowed = the top tier (Maximum rewards). Once reached, no more
  // pieces can be added. (There is no per-product cap — the same product can be
  // added multiple times until this total is hit.)
  function maxTotal() {
    var n = Number(config.maxReq);
    return (isFinite(n) && n > 0) ? n : Infinity;
  }

  // Highest tier's bundle price (the "any N for X" headline price). Tiers are
  // sorted ascending, so the last one with a price is the top tier.
  function topBundlePrice() {
    var p = 0;
    tiers.forEach(function (t) { if (Number(t.bundlePrice) > 0) p = Number(t.bundlePrice); });
    return p;
  }
  // Replace __PRICE__ in the offer banner + countdown copy with the top bundle
  // price, formatted in the customer's active currency.
  function applyPriceTokens() {
    var p = topBundlePrice();
    if (!p) return;
    var priceStr = money(p);
    qsa('.bb__offer-title, .bb__offer-body, .bb__sale-title, .bb__sale-sub').forEach(function (el) {
      if (el.innerHTML && el.innerHTML.indexOf('__PRICE__') !== -1) {
        el.innerHTML = el.innerHTML.replace(/__PRICE__/g, priceStr);
      }
    });
  }

  /* ---------- animations ---------- */
  function flyDot(fromEl) {
    if (!fromEl || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
    var target = qs('[data-bb-total]');
    var bar = qs('[data-bb-bar-count]');
    if (bar && bar.offsetParent !== null) target = bar;
    if (!target || !target.getBoundingClientRect) return;
    var a = fromEl.getBoundingClientRect();
    var t = target.getBoundingClientRect();
    var d = document.createElement('div');
    d.className = 'bb-fly';
    d.style.left = (a.left + a.width / 2 - 7) + 'px';
    d.style.top = (a.top - 7) + 'px';
    document.body.appendChild(d);
    var dx = (t.left + t.width / 2) - (a.left + a.width / 2);
    var dy = (t.top + t.height / 2) - a.top;
    var anim = d.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: 'translate(' + dx * 0.5 + 'px,' + (dy * 0.4 - 60) + 'px) scale(1.35)', opacity: 1, offset: 0.55 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.3)', opacity: 0.2 }
      ],
      { duration: 700, easing: 'cubic-bezier(.4,0,.2,1)' }
    );
    anim.onfinish = function () { d.remove(); };
  }

  function tween(el, from, to) {
    if (!el) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = money(to); return; }
    var t0 = null, dur = 600;
    function step(ts) {
      if (t0 == null) t0 = ts;
      var k = Math.min(1, (ts - t0) / dur);
      var e = 1 - Math.pow(1 - k, 3);
      el.textContent = money(Math.round(from + (to - from) * e));
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  var toastTimer;
  function toast(title, desc) {
    var el = qs('[data-bb-toast]');
    if (!el || (!title && !desc)) return;
    var tt = qs('[data-bb-toast-title]'), td = qs('[data-bb-toast-desc]');
    if (tt) tt.textContent = title || '';
    if (td) td.textContent = desc || '';
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('show'); }, 2800);
  }

  function pulse(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  }

  /* ---------- panel / lines ---------- */
  function lineHTML(l) {
    var img = l.image ? '<img src="' + escapeAttr(l.image) + '" alt="">' : '';
    return (
      '<div class="bb__line" data-bb-line="' + escapeAttr(l.variantId) + '">' +
      '<div class="lt">' + img + '</div>' +
      '<div class="li"><h4>' + escapeHtml(l.title) + '</h4>' +
      (l.meta ? '<div class="lm">' + escapeHtml(l.meta) + '</div>' : '') +
      '<div class="lp">' + escapeHtml(money(l.price)) + '</div></div>' +
      '<div class="bb__stepper">' +
      '<button type="button" data-bb-step="-1" data-bb-line-key="' + escapeAttr(l.variantId) + '" aria-label="Decrease">−</button>' +
      '<b>' + l.qty + '</b>' +
      '<button type="button" data-bb-step="1" data-bb-line-key="' + escapeAttr(l.variantId) + '" aria-label="Increase">+</button>' +
      '</div></div>'
    );
  }

  // Bundle-mode anchor line: a locked (non-deletable) row at the top of the panel
  // that shows the reached tier's flat price, so the shopper sees where the total
  // comes from (the pieces below ride along free).
  function anchorLineHTML(t) {
    var tier = t.tier;
    var title = (tier.anchorTitle && String(tier.anchorTitle).trim()) || tier.name || str('bundleLineTitle') || 'Bundle';
    var img = tier.anchorImage
      ? '<img src="' + escapeAttr(tier.anchorImage) + '" alt="">'
      : '';
    var sub = String(str('bundleLineLabel') || 'Bundle price');
    return (
      '<div class="bb__line bb__line--anchor">' +
      '<div class="lt">' + img + '</div>' +
      '<div class="li"><h4>' + escapeHtml(title) + '</h4>' +
      '<div class="lm">' + escapeHtml(sub) + '</div>' +
      '<div class="lp">' + escapeHtml(money(t.total)) + '</div></div>' +
      '<span class="bb__lock" title="' + escapeAttr(str('bundleLockedNote') || 'Included in your bundle') + '" aria-hidden="true">🔒</span>' +
      '</div>'
    );
  }

  // Order the ladder segments ascending by their target count and (re)compute each
  // segment's start from the previous segment's end. This makes the ladder correct
  // regardless of how the merchant ordered the tier blocks in the theme editor.
  function initSegments() {
    var wrap = qs('[data-bb-segs]');
    if (!wrap) return;
    var segs = qsa('[data-bb-seg]', wrap);
    if (!segs.length) return;
    segs.sort(function (a, b) {
      return (parseInt(a.getAttribute('data-to'), 10) || 0) - (parseInt(b.getAttribute('data-to'), 10) || 0);
    });
    var prev = 0;
    segs.forEach(function (seg) {
      var to = parseInt(seg.getAttribute('data-to'), 10) || 0;
      seg.setAttribute('data-from', prev);
      wrap.appendChild(seg); // reflow into ascending visual order
      prev = to;
      // Show the tier's bundle PRICE (in the active currency) on the segment
      // instead of the % chip, when a bundle price is configured for that tier.
      var tier = null;
      for (var i = 0; i < tiers.length; i++) {
        if (Number(tiers[i].req) === to) { tier = tiers[i]; break; }
      }
      var lbl = qs('[data-bb-seg-label]', seg) || qs('b', seg);
      if (lbl && tier && Number(tier.bundlePrice) > 0) {
        lbl.textContent = money(tier.bundlePrice);
      }
    });
  }

  function renderProgress(t) {
    // segmented reward ladder: each segment fills as the count moves from `from`→`to`.
    qsa('[data-bb-seg]').forEach(function (seg) {
      var from = parseInt(seg.getAttribute('data-from'), 10) || 0;
      var to = parseInt(seg.getAttribute('data-to'), 10) || 0;
      var span = (to - from) || 1;
      var prog = Math.max(0, Math.min(1, (t.count - from) / span));
      var bar = qs('.bb__rl-bar i', seg);
      if (bar) bar.style.width = (prog * 100) + '%';
      var reached = t.count >= to;
      var active = t.count > from && t.count < to;
      seg.classList.toggle('reached', reached);
      seg.classList.toggle('active', active);
      // pop the segment the moment it becomes fully reached
      if (reached && t.count > prevCount && to > prevCount) pulse(seg, 'pulse');
    });

    var tierEl = qs('[data-bb-prog-tier]');
    if (tierEl) {
      var name = t.count === 0 ? str('progressStart') : (t.tier ? t.tier.name : str('progressStart'));
      // keep the count span intact
      var countSpan = qs('[data-bb-prog-count]');
      tierEl.childNodes[0].nodeValue = name + ' ';
      if (countSpan) {
        var word = t.count === 1 ? str('pieceSingular') : str('piecePlural');
        countSpan.textContent = '· ' + t.count + ' ' + word;
      }
    }
    var hint = progressHint(t);
    qsa('[data-bb-prog-hint], [data-bb-prog-hint-wide]').forEach(function (el) { el.textContent = hint; });

    renderMobileProgress(t);
  }

  // Dedicated mobile progress: current tier + price, one bar toward the top tier,
  // and a "Add N more → next tier for price" line.
  function renderMobileProgress(t) {
    var wrap = qs('[data-bb-mprog]');
    if (!wrap) return;
    var tierEl = qs('[data-bb-mprog-tier]', wrap);
    var priceEl = qs('[data-bb-mprog-price]', wrap);
    var fillEl = qs('[data-bb-mprog-fill]', wrap);
    var nextEl = qs('[data-bb-mprog-next]', wrap);

    // Title: current tier name + count.
    if (tierEl) {
      var name = t.count === 0 ? str('progressStart') : (t.tier ? t.tier.name : str('progressStart'));
      var word = t.count === 1 ? str('pieceSingular') : str('piecePlural');
      tierEl.textContent = name + ' · ' + t.count + ' ' + word;
    }
    // Current reached price (blank until a tier is reached).
    if (priceEl) {
      priceEl.textContent = (t.tier && Number(t.tier.bundlePrice) > 0) ? money(t.tier.bundlePrice) : '';
    }
    // Bar = overall progress toward the top tier (Maximum rewards).
    if (fillEl) {
      var max = Number(config.maxReq) || 1;
      var pct = Math.max(0, Math.min(100, (t.count / max) * 100));
      fillEl.style.width = pct + '%';
    }
    // Next-reward line.
    if (nextEl) {
      var next = null;
      for (var i = 0; i < tiers.length; i++) {
        if (tiers[i].req > t.count) { next = tiers[i]; break; }
      }
      if (!next) {
        nextEl.textContent = str('unlockedAll');
      } else {
        var remaining = next.req - t.count;
        var nextName = next.nodeLabel || next.name || '';
        var nextPrice = Number(next.bundlePrice) > 0 ? money(next.bundlePrice) : '';
        nextEl.textContent = String(str('mobileNext') || 'Add __N__ more → __TIER__ for __PRICE__')
          .replace(/__N__/g, remaining)
          .replace(/__TIER__/g, nextName)
          .replace(/__PRICE__/g, nextPrice);
      }
    }
  }

  // Mobile bottom-bar helper text (shown only when the edit has pieces but no
  // savings yet). Accepts __N__ = pieces remaining to the next reward.
  function barProgressText(t) {
    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].req > t.count) { next = tiers[i]; break; }
    }
    if (!next) return str('unlockedAll');
    var remaining = Math.max(0, next.req - t.count);
    var tmpl = str('barProgress') || str('reserveNeedMore') || 'Add __N__ more';
    return String(tmpl).replace(/__N__/g, remaining);
  }

  function progressHint(t) {
    var maxReq = Number(config.maxReq) || 1;
    if (t.count >= maxReq) return str('unlockedAll');
    // next tier = smallest req greater than count
    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].req > t.count) { next = tiers[i]; break; }
    }
    if (!next) return str('unlockedAll');
    var remaining = next.req - t.count;
    return String(next.hint || '').replace(/__N__/g, remaining);
  }

  function renderPanel(t) {
    // totals
    var bigEl = qs('[data-bb-total]');
    if (bigEl) tween(bigEl, prevTotal, t.total);
    prevTotal = t.total;
    var wasEl = qs('[data-bb-was]');
    if (wasEl) wasEl.textContent = t.discount > 0 ? money(t.was) : '';

    // savebar
    var sb = qs('[data-bb-savebar]');
    if (sb) {
      if (t.discount > 0) {
        sb.hidden = false;
        var sv = qs('[data-bb-savings]'); if (sv) sv.textContent = money(t.discount);
        var pc = qs('[data-bb-savings-pct]');
        if (pc) pc.textContent = (t.tier && t.tier.pctLabel) ? t.tier.pctLabel : (t.pct + '% off');
      } else {
        sb.hidden = true;
      }
    }

    // lines
    var linesEl = qs('[data-bb-lines]');
    if (linesEl) {
      var ls = lines();
      if (!ls.length) {
        linesEl.innerHTML = '<div class="bb__empty"><p>' + escapeHtml(str('emptyEdit')) + '</p></div>';
      } else {
        // In bundle mode, once a tier is reached, wrap the locked anchor header
        // AND the chosen pieces inside ONE bordered bundle group (with a
        // separator) so the shopper sees the pieces belong to the bundle.
        var showAnchor = t.bundle && t.tier && Number(t.tier.bundlePrice) > 0;
        if (showAnchor) {
          linesEl.innerHTML =
            '<div class="bb__bundle-group">' +
              anchorLineHTML(t) +
              '<div class="bb__bundle-sep"></div>' +
              '<div class="bb__bundle-items">' + ls.map(lineHTML).join('') + '</div>' +
            '</div>';
        } else {
          linesEl.innerHTML = ls.map(lineHTML).join('');
        }
      }
    }

    // perks
    var perksEl = qs('[data-bb-perks]');
    if (perksEl && config.perks) {
      perksEl.innerHTML = config.perks.map(function (p) {
        var on = t.count >= p.req;
        var remain = on ? '' : ' · ' + (p.req - t.count) + ' more';
        var label = on ? '<b>' + escapeHtml(p.text) + '</b>' : escapeHtml(p.text) + remain;
        return '<div class="bb__perk' + (on ? ' on' : '') + '"><span class="pk"></span><span>' + label + '</span></div>';
      }).join('');
    }

    // reserve button
    var minReq = Number(str('reserveMin')) || 1;
    var rs = qs('[data-bb-reserve]');
    var rsLabel = qs('[data-bb-reserve-label]');
    if (rs && rsLabel) {
      var ready = t.count >= minReq;
      rs.disabled = !ready;
      if (!ready) {
        rsLabel.textContent = str('reserveNeedMore').replace(/__N__/g, Math.max(0, minReq - t.count));
      } else {
        rsLabel.textContent = str('reserveReady') + ' · ' + money(t.total);
      }
    }

    // shipping note
    var shipEl = qs('[data-bb-ship]');
    if (shipEl) {
      var target = Number(str('shipTarget')) || 0;
      if (target <= 0) {
        shipEl.textContent = '';
        shipEl.style.display = 'none';
      } else {
        shipEl.style.display = '';
        shipEl.textContent = t.count >= target
          ? str('shipDone')
          : str('shipNeed').replace(/__N__/g, Math.max(0, target - t.count));
      }
    }

    // mobile bar
    var barCount = qs('[data-bb-bar-count]');
    var barTotal = qs('[data-bb-bar-total]');
    var barWas = qs('[data-bb-bar-was]');
    var barSave = qs('[data-bb-bar-save]');
    var bar = qs('[data-bb-bar]');
    if (bar) bar.classList.toggle('empty', t.count === 0);
    if (barCount) {
      barCount.textContent = t.count;
      if (t.count > prevCount) pulse(barCount, 'bump');
    }
    if (barTotal) barTotal.textContent = money(t.total);
    if (barWas) barWas.textContent = t.discount > 0 ? money(t.was) : '';
    if (barSave) {
      if (t.count === 0) {
        // Empty edit → no helper text at all (just hide it).
        barSave.textContent = '';
        barSave.style.display = 'none';
      } else {
        barSave.style.display = '';
        if (t.discount > 0) {
          barSave.textContent = str('savingPrefix') + ' ' + money(t.discount) + ' · ' + (t.pct + '% off');
        } else {
          // No savings yet → show how many more pieces to the next reward.
          // The bar-progress copy accepts __N__ (pieces remaining).
          barSave.textContent = barProgressText(t);
        }
      }
    }
  }

  function render() {
    var t = totals();
    renderProgress(t);
    renderPanel(t);
    // refresh card add-button states
    qsa('[data-bb-card]').forEach(function (card) {
      var entry = products[card.getAttribute('data-bb-card')];
      if (entry) refreshCardState(card, entry);
    });
    // keep the quick-view modal (if open) consistent with the current edit
    if (qvEntry) refreshQuickView();
    // tier-unlock celebration — always reflect the CURRENT tier, falling back to
    // the tier name / discount label so a higher tier can't keep showing a stale
    // lower-tier toast when its unlock text is left blank.
    if (t.tierIdx > prevTierIdx && t.tier) {
      // Tokens: __PRICE__ = this tier's bundle price (active currency),
      //         __N__ = pieces required for this tier.
      var bp = Number(t.tier.bundlePrice) > 0 ? money(t.tier.bundlePrice) : '';
      var nStr = String(Number(t.tier.req) || 0);
      function fillTokens(s) {
        return String(s).replace(/__PRICE__/g, bp).replace(/__N__/g, nStr);
      }
      var unlockTitle = fillTokens(t.tier.unlockTitle || t.tier.name || '');
      var unlockDesc;
      if (t.tier.unlockDesc) {
        unlockDesc = fillTokens(t.tier.unlockDesc);
      } else if (t.bundle && bp) {
        unlockDesc = bp; // bundle mode, no custom text → show the flat price
      } else {
        unlockDesc = t.tier.pctLabel || (t.pct ? t.pct + '% off' : '');
      }
      toast(unlockTitle, unlockDesc);
      pulse(qs('[data-bb-savebar]'), 'flash');
    }
    prevCount = t.count;
    prevTierIdx = t.tierIdx;
    saveState();
  }

  /* ---------- persistence (localStorage) ---------- */
  // The shopper's in-progress edit survives reloads / return visits. We store only
  // {variantId, qty} and re-hydrate against the live catalog so prices, titles and
  // availability are always current (and removed/sold-out items silently drop).
  function storageKey() {
    var id = (root && root.getAttribute('data-section-id')) || 'default';
    return 'bb_edit_' + id;
  }
  function saveState() {
    try {
      var ls = lines();
      if (!ls.length) { localStorage.removeItem(storageKey()); return; }
      var data = ls.map(function (l) { return { v: l.variantId, q: l.qty }; });
      localStorage.setItem(storageKey(), JSON.stringify(data));
    } catch (e) {}
  }
  function clearSavedState() {
    try { localStorage.removeItem(storageKey()); } catch (e) {}
  }
  function findVariant(variantId) {
    var vid = String(variantId);
    var keys = Object.keys(products);
    for (var i = 0; i < keys.length; i++) {
      var entry = products[keys[i]];
      var vars = entry.product.variants || [];
      for (var j = 0; j < vars.length; j++) {
        if (String(vars[j].id) === vid) return { entry: entry, variant: vars[j] };
      }
    }
    return null;
  }
  function loadSavedState() {
    var out = {};
    try {
      var raw = localStorage.getItem(storageKey());
      if (!raw) return out;
      var data = JSON.parse(raw);
      if (!Array.isArray(data)) return out;
      data.forEach(function (item) {
        var found = findVariant(item && item.v);
        if (!found || !found.variant.available) return; // drop removed / sold-out
        var qty = Math.max(0, parseInt(item.q, 10) || 0);
        if (!qty) return;
        var v = found.variant, p = found.entry.product;
        out[v.id] = {
          variantId: v.id,
          productId: p.id,
          title: p.title,
          meta: cleanMeta(variantMeta(p, v)),
          image: (v.image || p.image) || '',
          price: v.price,
          compare: v.compare || 0,
          qty: qty
        };
        // reflect the restored choice in the card's swatches
        (v.options || []).forEach(function (val, idx) { found.entry.sel[idx] = val; });
        found.entry.variant = v;
      });
    } catch (e) {}
    return out;
  }

  /* ---------- edit mutations ---------- */
  // Remove a product from the edit entirely (all its variant lines). Used by the
  // card's in-edit "Remove" toggle.
  function removeProduct(entry) {
    if (!entry) return;
    var pid = entry.product.id;
    Object.keys(state).forEach(function (vid) {
      if (state[vid] && state[vid].productId === pid) delete state[vid];
    });
    if (!lines().length) state = {};
    render();
  }

  function addEntry(entry, fromEl) {
    var v = resolveVariant(entry);
    if (!v || !v.available) return;
    // Only the bundle's TOTAL max (top tier) caps the edit — the same product can
    // be added multiple times until that total is reached.
    if (totals().count >= maxTotal()) { toast('', str('bundleFull') || str('unlockedAll')); return; }
    if (!state[v.id]) {
      state[v.id] = {
        variantId: v.id,
        productId: entry.product.id,
        title: entry.product.title,
        meta: cleanMeta(variantMeta(entry.product, v)),
        image: (v.image || entry.product.image) || '',
        price: v.price,
        compare: v.compare || 0,
        qty: 0
      };
    }
    state[v.id].qty++;
    flyDot(fromEl);
    render();
  }
  function step(variantId, delta) {
    var l = state[variantId];
    if (!l) return;
    // Only the bundle's total max caps quantity increases (no per-product cap).
    if (delta > 0) {
      if (totals().count >= maxTotal()) return;
    }
    l.qty = (Number(l.qty) || 0) + delta;
    if (l.qty <= 0) delete state[variantId];
    // Hard guarantee: once the edit is empty, wipe state entirely so no stale
    // line can ever resurface when the next product is added.
    if (!lines().length) state = {};
    render();
  }

  /* ---------- commit to Shopify cart ---------- */
  function reserve() {
    var ls = lines();
    if (!ls.length) return;
    var rs = qs('[data-bb-reserve]');
    if (rs) rs.classList.add('loading');
    var t0 = totals();
    var items;
    if (t0.bundle && t0.tier && t0.tier.anchorId) {
      // Fixed-price bundle: one priced "anchor" line carries the flat tier price;
      // the chosen pieces ride along (their variants should be the 0.00 bundle
      // variants so they show as FREE at checkout — like the competitor).
      items = [{ id: t0.tier.anchorId, quantity: 1, properties: { '_bundle': t0.tier.name } }];
      ls.forEach(function (l) {
        items.push({ id: l.variantId, quantity: l.qty, properties: { '_bundle': t0.tier.name } });
      });
    } else {
      items = ls.map(function (l) { return { id: l.variantId, quantity: l.qty }; });
    }
    // Locale/market-aware: Shopify Markets prefixes routes (e.g. /en-sa/), so a bare /cart/add.js 404s.
    var addUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/add.js';
    fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        // Always read the body — Shopify returns a JSON error (status/message/
        // description) on failure (e.g. an invalid or sold-out variant id).
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok) {
            var msg = body && (body.description || body.message) ? (body.description || body.message) : ('HTTP ' + r.status);
            // Log the full payload so the merchant can see WHICH id failed.
            try { console.error('[bundle-builder] /cart/add failed:', msg, '\nItems sent:', items, '\nShopify said:', body); } catch (e) {}
            var err = new Error(msg); err.bbDescription = msg; throw err;
          }
          return body;
        });
      })
      .then(function () {
        try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e) {}
        // The edit is now committed to the real cart — drop the saved draft so a
        // return visit doesn't restore an order the shopper already reserved.
        clearSavedState();
        var after = config.afterReserve || 'checkout';
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        // Per-tier discount code: apply the highest-reached tier's code, if any.
        var reachedTier = totals().tier;
        var code = (reachedTier && reachedTier.code) ? String(reachedTier.code).trim() : '';
        // /discount/CODE?redirect=… sets the cart's discount and carries it to the destination.
        function withCode(dest) {
          return code ? root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(dest) : null;
        }
        if (after === 'drawer') {
          if (rs) rs.classList.remove('loading');
          try { document.dispatchEvent(new CustomEvent('cart:open')); } catch (e2) {}
          try { document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: {} })); } catch (e3) {}
        } else if (after === 'cart') {
          window.location.href = withCode('/cart') || (root + 'cart');
        } else {
          // Go to checkout (default). /checkout is locale/market-safe via the cart cookie.
          window.location.href = withCode('/checkout') || '/checkout';
        }
      })
      .catch(function (err) {
        if (rs) rs.classList.remove('loading');
        var detail = (err && err.bbDescription) ? err.bbDescription : str('errorAdd');
        toast('', detail);
        var sb = qs('[data-bb-reserve-label]');
        if (sb) sb.textContent = str('errorAdd');
      });
  }

  /* ---------- scroll to the "Begin your edit" ladder (CTA buttons) ---------- */
  function scrollToGrid() {
    // Prefer the progress ladder ("Begin your edit"); fall back to the grid.
    var prog = qs('[data-bb-progress]');
    var toProgress = prog && prog.offsetParent !== null;
    var target = toProgress ? prog : (qs('[data-bb-grid]') || qs('.bb__toolbar') || qs('.bb__main'));
    if (!target || !target.getBoundingClientRect) return;
    // Landing on the ladder itself: small offset. Landing on the grid: clear the sticky ladder.
    var offset = toProgress ? 8 : ((prog && prog.offsetParent !== null ? prog.offsetHeight : 0) + 16);
    var y = (window.pageYOffset || document.documentElement.scrollTop) + target.getBoundingClientRect().top - offset;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
  }

  /* ---------- bottom-sheet open/close (mobile) ---------- */
  function openSheet() {
    var w = qs('[data-bb-panel-wrap]'), s = qs('[data-bb-scrim]');
    if (w) w.classList.add('open');
    if (s) s.classList.add('open');
  }
  function closeSheet() {
    var w = qs('[data-bb-panel-wrap]'), s = qs('[data-bb-scrim]');
    if (w) w.classList.remove('open');
    if (s) s.classList.remove('open');
  }

  /* ---------- events (delegated) ---------- */
  function bind() {
    if (bind._bound) return;
    bind._bound = true;

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;
      if (!t.closest('#NuceryBundleBuilder')) {
        // scrim/bar live inside the section too, so this also covers them
        return;
      }
      if (t.closest('[data-bb-scroll]')) { scrollToGrid(); return; }
      var chip = t.closest('[data-bb-chip]');
      if (chip) {
        qsa('[data-bb-chip]').forEach(function (c) { c.classList.remove('active'); });
        chip.classList.add('active');
        filterGrid(chip.getAttribute('data-bb-chip'));
        return;
      }
      var sw = t.closest('[data-bb-swatch]');
      if (sw) {
        if (sw.getAttribute('aria-disabled') === 'true') return;
        var card = sw.closest('[data-bb-card]');
        var entry = card && products[card.getAttribute('data-bb-card')];
        if (!entry) return;
        entry.sel[parseInt(sw.getAttribute('data-bb-swatch'), 10)] = sw.getAttribute('data-bb-value');
        resolveVariant(entry);
        refreshCardState(card, entry);
        return;
      }
      // Quick-view swatch: change the modal entry's variant (also updates the
      // matching grid card, since they share the same entry object).
      var qsw = t.closest('[data-bb-qv-swatch]');
      if (qsw) {
        if (qsw.getAttribute('aria-disabled') === 'true' || !qvEntry) return;
        qvEntry.sel[parseInt(qsw.getAttribute('data-bb-qv-swatch'), 10)] = qsw.getAttribute('data-bb-value');
        resolveVariant(qvEntry);
        refreshQuickView();
        var qcard = qs('[data-bb-card="' + qvEntry.product.id + '"]');
        if (qcard) refreshCardState(qcard, qvEntry);
        return;
      }
      // Quick-view "Add to cart" → add the selected variant to the edit, then
      // close the modal so the shopper returns to the grid.
      var qvAdd = t.closest('[data-bb-qv-add]');
      if (qvAdd) {
        if (qvAdd.disabled || !qvEntry) return;
        addEntry(qvEntry, qvAdd);
        closeQuickView();
        return;
      }
      if (t.closest('[data-bb-qv-close]')) { closeQuickView(); return; }
      var quick = t.closest('[data-bb-quick]');
      if (quick) {
        var qc = quick.closest('[data-bb-card]');
        var qe = qc && products[qc.getAttribute('data-bb-card')];
        if (qe) openQuickView(qe);
        return;
      }
      // Mobile: tapping the card image opens quick view (the icon button is hidden).
      var thumb = t.closest('[data-bb-thumb]');
      if (thumb && !t.closest('[data-bb-dots]') && window.matchMedia && window.matchMedia('(max-width: 999px)').matches) {
        var tc = thumb.closest('[data-bb-card]');
        var te = tc && products[tc.getAttribute('data-bb-card')];
        if (te) { openQuickView(te); return; }
      }
      var add = t.closest('[data-bb-add]');
      if (add) {
        if (add.disabled) return;
        var ac = add.closest('[data-bb-card]');
        var ae = ac && products[ac.getAttribute('data-bb-card')];
        if (!ae) return;
        // At the top tier (all rewards unlocked) the in-edit button is "Remove".
        // Otherwise it always adds (same product can be added again).
        var pInEdit = Object.keys(state).some(function (vid) {
          return state[vid].productId === ae.product.id && state[vid].qty > 0;
        });
        if (totals().count >= maxTotal() && pInEdit) removeProduct(ae);
        else addEntry(ae, add);
        return;
      }
      var stepBtn = t.closest('[data-bb-step]');
      if (stepBtn) {
        step(stepBtn.getAttribute('data-bb-line-key'), parseInt(stepBtn.getAttribute('data-bb-step'), 10));
        return;
      }
      if (t.closest('[data-bb-reserve]')) {
        if (!qs('[data-bb-reserve]').disabled) reserve();
        return;
      }
      if (t.closest('[data-bb-close]') || t.closest('[data-bb-scrim]')) { closeSheet(); return; }
      if (t.closest('[data-bb-bar]')) { openSheet(); return; }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeSheet(); closeQuickView(); }
      if ((e.key === 'Enter' || e.key === ' ') && e.target && e.target.closest && e.target.closest('[data-bb-bar]')) {
        e.preventDefault();
        openSheet();
      }
    });

    window.addEventListener('scroll', function () {
      var prog = qs('[data-bb-progress]');
      if (prog) prog.classList.toggle('float', window.scrollY > 120);
    });
  }

  /* ---------- init ---------- */
  function syncFromConfig() {
    config = readJSON('script[data-bb-config]') || { strings: {} };
    config.strings = config.strings || {};
    catalog = readJSON('script[data-bb-catalog]') || { products: [] };
    tiers = (config.tiers || []).slice().sort(function (a, b) { return a.req - b.req; });
    prevCount = 0;
    prevTierIdx = -1;
    prevTotal = 0;
    buildProducts();
    // Re-hydrate the shopper's saved edit (needs the catalog built first).
    state = loadSavedState();
    buildGrid();
    initSegments();
    render();
    initCountdowns();
    applyPriceTokens();
  }

  function init() {
    if (!ensureRoot()) return;
    syncFromConfig();
    bind();
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:select', onSectionLoad);
  }

  function onSectionLoad(e) {
    var sec = e && e.target;
    if (!sec || typeof sec.querySelector !== 'function') return;
    if (!sec.querySelector('#NuceryBundleBuilder') && sec.id !== 'NuceryBundleBuilder') return;
    if (!ensureRoot()) return;
    syncFromConfig();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
